import { filterResidentsByCategories } from '@/utils/residentCategories';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { Alert } from '@mui/material';
import { useSelector } from 'react-redux';
import { NotificationResidentListHeader } from '@/components/notificationResidentListHeader';
import api from '@/api';
import { IGetNotificationRes } from '@/types/api/notification/getNotification';
import { NotificationListItem } from './notificationListItem';
import { selfDataSelector, showMedicalContentSelector } from '@/redux/config/selfData';
import { filterConditionSelector } from '@/redux/config/residentListSettings';
import { usePermissionScopedData } from '@/hooks/usePermissionScopedData';
import { IGetResidentActiveRes } from '@/types/api/resident/getResidentActive';
import { IGetResidentRes } from '@/types/api/resident/getResident';
import { getNotificationFilterOptions } from '@/constants/notificationFilters';
import {
  ResidentFilters,
  matchesNotificationFilter,
} from '@/utils/notificationFilters';

interface ResidentNotification extends IGetNotificationRes {
  residentName: string;
  residentLastName: string;
}

const EMPTY_DATA: {
  notifications: IGetNotificationRes[];
  residents: IGetResidentRes[];
  activeResidents: IGetResidentActiveRes[];
} = { notifications: [], residents: [], activeResidents: [] };

const NotificationListContainer = css`
  border-radius: 8px;
  overflow: hidden;
`;

const Container = css`
  padding: 0 16px 16px;
`;

const EmptyResidentContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 16px;
`;

// アカウント・施設・サービスが変わった場合は選択中の利用者と取得結果も初期化する。
export const Notification: React.VFC = () => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { selfUser } = useSelector(selfDataSelector);
  if (!selfUser.serviceType) return null;
  const scope = `${nursingHomeId}:${selfUser.id}:${selfUser.serviceType}:${selfUser.nursingHomeUnitId ?? 'all'}`;
  return <NotificationContent key={`${scope}:${selfUser.hasUnitPerm}`} storageKey={`notificationCategories:${scope}`} />;
};

const NotificationContent: React.VFC<{ storageKey: string }> = ({ storageKey }) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { selfUser, selfUnits } = useSelector(selfDataSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const { categories: sharedCategories } = useSelector(filterConditionSelector);
  const { t } = useTranslation();
  const permittedUnitId = selfUser.hasUnitPerm ? selfUser.nursingHomeUnitId : null;
  const [filterState, setFilterState] = React.useState<ResidentFilters>(() => {
    let categories = sharedCategories.filter((value) => !selfUser.hasUnitPerm || !value.startsWith('unit-'));
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
      if (Array.isArray(saved)) {
        categories = saved.filter((value): value is string => typeof value === 'string' && (
          value === 'deviceAssigned' || value === 'deviceAttached' ||
          (!selfUser.hasUnitPerm && /^unit-\d+$/.test(value))
        ));
      }
    } catch {
      // 以前の保存値が壊れていた場合は既存の一覧設定で初期化する。
    }
    return { categories: Array.from(new Set(categories)), residentId: null, notificationTypes: [] };
  });
  const notificationOptions = getNotificationFilterOptions(showMedicalContent);
  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(filterState.categories));
    } catch {
      // 保存できない環境でも画面内の絞り込みは利用できる。
    }
  }, [filterState.categories, storageKey]);

  const { data: fetchedData, isFetching, hasFetchError, refresh } = usePermissionScopedData({
    nursingHomeId,
    userId: selfUser.id,
    serviceType: selfUser.serviceType,
    permittedUnitId,
    fetchData: async (unitId) => {
      // 閲覧権限の範囲から尿・便それぞれ最新50件を取得する。選択ユニットでは再取得しない。
      const notificationClasses = showMedicalContent ? [0] : [0, 1];
      const [notificationGroups, residents, activeResidents] = await Promise.all([
        Promise.all(notificationClasses.map((notificationClass) =>
          api.get('/nursing-homes/:nursingHomeId/notifications', {
            params: {
              nursingHomeId,
              limit: 50,
              sort: 'desc',
              ...(unitId !== null ? { nursingHomeUnitId: unitId } : {}),
              ...(notificationClass === 1 ? { notificationClass } : {}),
            },
          }),
        )),
        api.get('/nursing-homes/:nursingHomeId/residents', {
          params: {
            nursingHomeId,
            limit: 10000,
            // APIのクエリ名はsnake_case。
            // eslint-disable-next-line camelcase
            ...(permittedUnitId !== null ? { nursing_home_unit_id: permittedUnitId } : {}),
          },
        }),
        api.get('/nursing-homes/:nursingHomeId/residents/active', { params: { nursingHomeId } }),
      ]);
      const notifications = Array.from(new Map(notificationGroups.flat().map((notification) => [
        `${notification.notificationClass}:${notification.id}`, notification,
      ])).values()).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      return { notifications, residents, activeResidents };
    },
  });
  const data = fetchedData ?? EMPTY_DATA;

  const residentsForHeaderFilter = React.useMemo(() => filterResidentsByCategories(
    data.residents, data.activeResidents, filterState.categories, permittedUnitId,
  ), [data.residents, data.activeResidents, filterState.categories, permittedUnitId]);

  React.useEffect(() => {
    if (isFetching || hasFetchError) return;
    setFilterState((previous) => previous.residentId !== null &&
      !residentsForHeaderFilter.some((resident) => resident.id === previous.residentId)
      ? { ...previous, residentId: null }
      : previous);
  }, [residentsForHeaderFilter, isFetching, hasFetchError]);

  const handleChangeFilters = (filters: ResidentFilters) => {
    const candidates = filterResidentsByCategories(data.residents, data.activeResidents, filters.categories, permittedUnitId);
    setFilterState({
      ...filters,
      residentId: candidates.some((resident) => resident.id === filters.residentId) ? filters.residentId : null,
    });
  };

  const filteredResidentNotifications = React.useMemo<ResidentNotification[]>(() => {
    const residentById = new Map(residentsForHeaderFilter.map((resident) => [resident.id, resident]));
    const activeIds = new Set(data.activeResidents.map((resident) => resident.id));
    return data.notifications.filter((notification) =>
      residentById.has(notification.residentId) && activeIds.has(notification.residentId) &&
      (filterState.residentId === null || notification.residentId === filterState.residentId) &&
      matchesNotificationFilter(notification.notificationType, filterState.notificationTypes, notificationOptions),
    ).map((notification) => ({
      ...notification,
      residentName: residentById.get(notification.residentId)?.name ?? '',
      residentLastName: residentById.get(notification.residentId)?.lastName ?? '',
    }));
  }, [data, residentsForHeaderFilter, filterState.residentId, filterState.notificationTypes, notificationOptions]);

  return (
    <div css={Container}>
      <NotificationResidentListHeader
        title={t('notification', '通知')}
        units={selfUnits}
        residents={residentsForHeaderFilter}
        filters={filterState}
        onUpdate={refresh}
        onChangeFilters={handleChangeFilters}
      />
      {hasFetchError ? (
        <Alert severity='error'>{t('@failedToLoadNotifications', '通知の取得に失敗しました')}</Alert>
      ) : !isFetching && !filteredResidentNotifications.length ? (
        <div css={EmptyResidentContainer}>{t('noNotification', '通知はありません')}</div>
      ) : (
        <div css={NotificationListContainer}>
          {filteredResidentNotifications.map((notification) => (
            <NotificationListItem key={`${notification.notificationClass}:${notification.id}`} notification={notification} />
          ))}
        </div>
      )}
    </div>
  );
};
